import { getCommonAnalyticsFields } from "../../analytics/analytics_identity.js";
import { AnalyticStore } from "../../analytics/analytic_store.js";
import { getAthenaTransport } from "../../analytics/index.js";
import { AuthSessionStore } from "../../api/auth_session.js";
import { GlobalSettings } from "../../global_settings.js";
import { AppsFlyer } from "../../plugins/apps_flyer_plugin.js";
import { UtilityPlugin } from "../../plugins/utility_plugin.js";
import { S2SEventContextService } from "../../s2s/s2s_event_context.js";
import { TelegramAPISettings } from "../../telegram_api_settings.js";
import { ensureDeviceId } from "../../utils/device_id.js";
import { VersionLoader } from "../../version_loader.js";
import { ToClientCommand, ToServerCommand } from "../constants.js";
import { CustomEventsController } from "./custom_events_controller.js";
import { SessionStartResponse } from "../data_structures/to_client/session_start_response.js";
import { SessionStartRequest } from "../data_structures/to_server/session_start_request.js";
import { SessionUpdateRequest } from "../data_structures/to_server/session_update_request.js";
import { ServerController } from "../server_controller.js";
import { CrashIdentity } from "../../crash/identity.js";

type SessionAttributes = {
  appsflyerid: string;
  consent_region: string;
  att_status: string;
  analytics_allowed: boolean;
  ad_measurement_allowed: boolean;
  ad_personalization_allowed: boolean;
};

export class SessionController
{
  private static readonly CONNECT_TIMEOUT_MS = 4_000;
  private static readonly HEARTBEAT_FAST_INTERVAL_MS = 500;
  private static readonly HEARTBEAT_SLOW_INTERVAL_MS = 5_000;
  private static readonly HEARTBEAT_FAST_PERIOD_MS = 30_000;

  private static server: ServerController | null = null;
  private static heartbeat_timer: ReturnType<typeof setTimeout> | null = null;
  private static heartbeat_started_at = 0;
  private static heartbeat_loop_id = 0;
  private static start_session_attempt_id = 0;
  private static start_attributes_key: string | null = null;
  private static synced_attributes_key: string | null = null;

  public static register( server: ServerController ): void
  {
    this.server = server;
    server.on( ToClientCommand.StartSessionResponse, reader =>
    {
      const response = new SessionStartResponse();
      response.deserialize( reader );

      if ( response.session_id )
      {
        const previous_session_id = AnalyticStore.getSessionIdFromServer();
        if ( response.session_id !== previous_session_id && this.start_attributes_key !== null )
          this.synced_attributes_key = `${response.session_id}|${this.start_attributes_key}`;
        void UtilityPlugin.reportAppStart( 'server_session', 'success' );
        AnalyticStore.setSessionIdFromServer( response.session_id );
        CrashIdentity.setSession( response.session_id );
        getAthenaTransport()?.setSessionId( response.session_id );
        CustomEventsController.markSessionReady();
        console.info( `[WebSocket] server session started: ${response.session_id}` );
        void this.syncSessionAttributes();
      }
      else
      {
        void UtilityPlugin.reportAppStart( 'server_session', 'failed' );
        CustomEventsController.markSessionUnavailable();
        console.warn( '[WebSocket] start-session response has no sessionid' );
      }
    } );
  }

  public static async sendStartSession( skip_fast_heartbeat = false ): Promise<void>
  {
    if ( !this.server ) return;
    const attempt_id = ++this.start_session_attempt_id;

    const customer_user_id = AuthSessionStore.getPlayerId();
    if ( !AuthSessionStore.isLoggedIn() || customer_user_id === null )
    {
      CustomEventsController.markSessionUnavailable();
      console.warn( '[WebSocket] start-session skipped: player is not logged in' );
      return;
    }

    CustomEventsController.markSessionUnavailable();
    void UtilityPlugin.reportAppStart( 'server_session', 'start' );
    await VersionLoader.load();
    const common_fields = getCommonAnalyticsFields();
    const app_start_info = await UtilityPlugin.getAppStartInfo();
    const attributes = await this.buildSessionAttributes();
    const locale = TelegramAPISettings.getUserLanguage() ?? navigator.language ?? '';
    const device_id = app_start_info?.deviceId ?? await ensureDeviceId();
    S2SEventContextService.initialize( {
      device: device_id,
      appsflyer_id: attributes.appsflyerid,
    } );

    const payload: Record<string, unknown> = {
      device: device_id,
      gamelocale: String( locale ),
      isqa: !GlobalSettings.IS_PROD,
      platform: common_fields.platform,
      sessionid_client: AnalyticStore.getSessionId(),
      signuptimestamp: common_fields.signuptimestamp,
      sourceid: ( globalThis as typeof globalThis & { __launchedFromPush?: boolean } ).__launchedFromPush ? 'push' : 'icon',
      userlevel: common_fields.userlevel,
      version: normalizeVersion( VersionLoader.getVersion() ),
      customeruserid: customer_user_id,
      ...attributes,
    };

    if ( attempt_id !== this.start_session_attempt_id ) return;

    const connected = await this.server.waitForConnectionWithTimeout( this.CONNECT_TIMEOUT_MS );
    if ( !connected )
    {
      void UtilityPlugin.reportAppStart( 'server_session', 'failed' );
      console.warn( '[WebSocket] start-session delayed: connection timeout' );
      this.server.onNextConnection( () =>
      {
        if ( attempt_id === this.start_session_attempt_id )
          void this.sendStartSession( skip_fast_heartbeat );
      } );
      return;
    }

    if ( attempt_id !== this.start_session_attempt_id ) return;

    const request = new SessionStartRequest(
      common_fields.userid,
      payload,
      AnalyticStore.getSessionIdFromServer(),
      AuthSessionStore.getToken() ?? undefined,
    );
    if ( !this.server.sendRequest( request, ToServerCommand.StartSession ) )
    {
      void UtilityPlugin.reportAppStart( 'server_session', 'failed' );
      return;
    }

    this.start_attributes_key = JSON.stringify( attributes );
    this.startHeartbeatLoop( skip_fast_heartbeat );
  }

  public static async syncSessionAttributes(): Promise<void>
  {
    if ( !this.server || !CustomEventsController.isSessionReady() ) return;
    const attributes = await this.buildSessionAttributes();
    const session_id = AnalyticStore.getSessionIdFromServer();
    if ( !session_id || !CustomEventsController.isSessionReady() ) return;

    const key = `${session_id}|${JSON.stringify( attributes )}`;
    if ( key === this.synced_attributes_key ) return;
    if ( this.server.sendRequest( new SessionUpdateRequest( attributes ), ToServerCommand.UpdateSession ) )
      this.synced_attributes_key = key;
  }

  private static async buildSessionAttributes(): Promise<SessionAttributes>
  {
    const appsflyer_id = await AppsFlyer.getAppsFlyerUID() ?? '';
    const consent = S2SEventContextService.getConsentSnapshot();
    return {
      appsflyerid: appsflyer_id,
      consent_region: consent.region,
      att_status: consent.att_status,
      analytics_allowed: consent.analytics_allowed,
      ad_measurement_allowed: consent.ad_measurement_allowed,
      ad_personalization_allowed: consent.ad_personalization_allowed,
    };
  }

  public static async sendRewardImpressionPerSession(): Promise<void>
  {
    await this.sendConnectedCommand( ToServerCommand.RewardImpressionPerSession );
  }

  public static async sendInterstitialImpressionPerSession(): Promise<void>
  {
    await this.sendConnectedCommand( ToServerCommand.InterImpressionPerSession );
  }

  private static async sendHeartbeatToServer(): Promise<void>
  {
    await this.sendConnectedCommand( ToServerCommand.Heartbeat );
  }

  private static async sendConnectedCommand( command: ToServerCommand ): Promise<void>
  {
    if ( !this.server ) return;
    const connected = await this.server.waitForConnectionWithTimeout( this.CONNECT_TIMEOUT_MS );
    if ( connected ) this.server.sendRequest( null, command );
  }

  private static startHeartbeatLoop( skip_fast_phase: boolean ): void
  {
    this.stopHeartbeatLoop();
    this.heartbeat_started_at = skip_fast_phase
      ? performance.now() - this.HEARTBEAT_FAST_PERIOD_MS
      : performance.now();
    const loop_id = ++this.heartbeat_loop_id;

    const tick = async (): Promise<void> =>
    {
      if ( loop_id !== this.heartbeat_loop_id ) return;
      await this.sendHeartbeatToServer();
      if ( loop_id !== this.heartbeat_loop_id ) return;

      const elapsed_ms = performance.now() - this.heartbeat_started_at;
      const interval_ms = elapsed_ms < this.HEARTBEAT_FAST_PERIOD_MS
        ? this.HEARTBEAT_FAST_INTERVAL_MS
        : this.HEARTBEAT_SLOW_INTERVAL_MS;
      this.heartbeat_timer = setTimeout( () => void tick(), interval_ms );
    };

    void tick();
  }

  private static stopHeartbeatLoop(): void
  {
    ++this.heartbeat_loop_id;
    if ( this.heartbeat_timer ) clearTimeout( this.heartbeat_timer );
    this.heartbeat_timer = null;
  }
}

export function normalizeVersion( value: unknown ): string
{
  const raw = String( value ?? '' ).trim();
  if ( !raw || /^\d{4}-\d{2}-\d{2}(?:t|$)/i.test( raw ) ) return '0_0_0';
  return raw.replace( /[^a-zA-Z0-9]+/g, '_' ).replace( /^_+|_+$/g, '' ) || '0_0_0';
}
